/** Column + form field definitions per admin console panel. */

const DEFAULT_ACTIONS = {
  view: true,
  modify: true,
  delete: true,
  add: true,
  review: false,
  approve: false,
  access: false,
  move: false,
};

/** Resolve which row actions apply for a panel (last column). */
export function rowActionsFor(panelConfig) {
  const readOnly = panelConfig?.readOnly;
  const actions = { ...DEFAULT_ACTIONS, ...(panelConfig?.actions || {}) };
  if (readOnly) {
    actions.modify = false;
    actions.delete = false;
    actions.add = false;
  }
  if (panelConfig?.hideAdd) actions.add = false;
  if (panelConfig?.hideDelete) actions.delete = false;
  if (panelConfig?.hideModify) actions.modify = false;
  return actions;
}

/** Whether create/modify/delete/governance actions need human review before applying. */
export function approvalRequiredFor(panelConfig, mode) {
  const cfg = panelConfig?.requiresApproval || {};
  if (mode === "create") return !!cfg.create;
  if (mode === "edit") return !!cfg.modify;
  if (mode === "delete") return !!cfg.delete;
  if (mode === "access") return cfg.access !== false;
  if (mode === "move") return cfg.move !== false;
  if (mode === "approve") return cfg.approve !== false;
  if (mode === "review") return !!cfg.review;
  return false;
}

export const ACCESS_AUTHORITY_FIELDS = [
  { key: "auth_G1", label: "G1 authority", default: "NONE" },
  { key: "auth_G2", label: "G2 / MDR authority", default: "NONE" },
  { key: "auth_G4", label: "G4 authority", default: "NONE" },
  { key: "auth_ARCHITECTURE", label: "Architecture authority", default: "NONE" },
  { key: "auth_SECURITY", label: "Security authority", default: "NONE" },
  { key: "auth_DELIVERY_READINESS", label: "Delivery readiness", default: "NONE" },
  { key: "auth_G7", label: "G7 authority", default: "NONE" },
  { key: "auth_G8", label: "G8 authority", default: "NONE" },
];

export const PLATFORM_PANELS = {
  overview: {
    entityLabel: null,
    readOnly: true,
    kicker: "CONTROL PLANE",
    title: "Operator dashboard",
    detail: "Use the sidebar to manage organizations, tenants, subscriptions and platform settings.",
  },
  organizations: {
    entityLabel: "Organization",
    rowKey: "id",
    kicker: "ORGANIZATIONS",
    requiresApproval: { create: true, modify: true, delete: true },
    columns: [
      { key: "id", label: "ID" },
      { key: "name", label: "Name" },
      { key: "status", label: "Status" },
      { key: "products", label: "Products" },
    ],
    fields: [
      { key: "id", label: "Organization ID", required: true, placeholder: "ORG-NEW" },
      { key: "name", label: "Name", required: true },
      { key: "status", label: "Status", type: "select", options: ["live", "catalog", "suspended"] },
      { key: "products", label: "Products (comma-separated)" },
    ],
  },
  tenants: {
    entityLabel: "Tenant",
    rowKey: "code",
    pageSize: 10,
    kicker: "TENANTS",
    requiresApproval: { create: false, modify: true, delete: true },
    dependencies: [
      { key: "organization_id", label: "Organization", required: true, help: "Control-plane organization id (e.g. ORG-NRG). Not the data-plane estate id ORG-NRG-01." },
      { key: "product", label: "Product", required: true, help: "Licensed product for this tenant." },
    ],
    columns: [
      { key: "code", label: "Code", width: "9%" },
      { key: "name", label: "Name", width: "16%" },
      { key: "organization", label: "Organization", width: "14%" },
      { key: "organization_id", label: "Org ID", width: "10%" },
      { key: "product", label: "Product", width: "12%" },
      { key: "subscription", label: "Subscription", width: "14%" },
      { key: "deployment", label: "Deployment", width: "11%", format: (v) => formatDeployment(v) },
      { key: "status", label: "Status", width: "8%" },
    ],
    fields: [
      { key: "code", label: "Tenant code", required: true, placeholder: "ABC-02" },
      { key: "name", label: "Name", required: true },
      { key: "organization_id", label: "Organization", required: true, type: "select" },
      { key: "product", label: "Product", default: "Modernization Advisor" },
      { key: "subscription", label: "Subscription" },
      { key: "deployment", label: "Deployment", type: "select", options: ["shared_saas", "dedicated_saas", "byoc"] },
      { key: "region", label: "Region" },
      { key: "data_residency", label: "Data residency" },
      { key: "status", label: "Status", type: "select", options: ["live", "catalog", "suspended", "DRAFT"] },
      { key: "tenant_admin", label: "Tenant admin email" },
    ],
  },
  subscriptions: {
    entityLabel: "Subscription",
    rowKey: "code",
    kicker: "SUBSCRIPTIONS / LICENSING",
    readOnly: true,
    hideAdd: true,
    detail: "Edit subscription labels on the Tenants panel. Payment/billing is deferred until Auth → Tenant → RBAC → Entitlement are complete.",
    columns: [
      { key: "code", label: "Tenant" },
      { key: "name", label: "Customer" },
      { key: "subscription", label: "Plan" },
      { key: "product", label: "Product" },
    ],
    fields: [
      { key: "code", label: "Tenant code", required: true },
      { key: "name", label: "Customer name", required: true },
      { key: "subscription", label: "Plan", required: true },
      { key: "product", label: "Product" },
    ],
  },
  users: {
    entityLabel: "User",
    rowKey: "email",
    kicker: "USERS & ADMINS",
    readOnly: true,
    hideAdd: true,
    detail: "Directory is read-only here. Invite users from the Tenant console.",
    requiresApproval: { create: true, modify: true, delete: true },
    dependencies: [{ key: "role", label: "Role", required: true, help: "Role determines console access and decision authority." }],
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "role", label: "Role" },
      { key: "scope", label: "Scope" },
    ],
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "email", label: "Email", required: true, type: "email" },
      { key: "role", label: "Role", required: true },
      { key: "scope", label: "Scope", default: "global" },
    ],
  },
  features: {
    entityLabel: "Feature",
    rowKey: "id",
    kicker: "FEATURES",
    readOnly: true,
    hideAdd: true,
    columns: [
      { key: "id", label: "ID" },
      { key: "label", label: "Feature" },
      { key: "state", label: "State" },
      { key: "now", label: "In POC", format: (v) => (v ? "Yes" : "No") },
    ],
    fields: [
      { key: "id", label: "Feature ID", required: true },
      { key: "label", label: "Label", required: true },
      { key: "state", label: "State", type: "select", options: ["on", "off", "catalog", "next"] },
      { key: "now", label: "Live in POC", type: "checkbox" },
    ],
  },
  deployment: {
    entityLabel: "Deployment",
    rowKey: "code",
    kicker: "DEPLOYMENT MODEL",
    readOnly: true,
    hideAdd: true,
    detail: "Change deployment model on the Tenants panel.",
    columns: [
      { key: "code", label: "Tenant" },
      { key: "name", label: "Name" },
      { key: "deployment", label: "Model" },
      { key: "data_residency", label: "Data residency" },
      { key: "region", label: "Region" },
    ],
    fields: [
      { key: "code", label: "Tenant code", required: true },
      { key: "name", label: "Name", required: true },
      { key: "deployment", label: "Model", type: "select", options: ["shared_saas", "dedicated_saas", "byoc"] },
      { key: "data_residency", label: "Data residency" },
      { key: "region", label: "Region" },
    ],
  },
  iam: {
    entityLabel: "IAM policy",
    rowKey: "id",
    kicker: "IAM / SSO",
    columns: [
      { key: "id", label: "Policy" },
      { key: "mode", label: "Mode" },
      { key: "detail", label: "Detail" },
    ],
    fields: [
      { key: "id", label: "Policy ID", required: true },
      { key: "mode", label: "Mode", required: true },
      { key: "detail", label: "Detail", type: "textarea" },
    ],
  },
  ai: {
    entityLabel: "AI setting",
    rowKey: "key",
    kicker: "AI CONFIGURATION",
    columns: [
      { key: "key", label: "Setting" },
      { key: "value", label: "Value" },
      { key: "note", label: "Note" },
    ],
    fields: [
      { key: "key", label: "Setting key", required: true },
      { key: "value", label: "Value", required: true },
      { key: "note", label: "Note" },
    ],
  },
  usage: {
    entityLabel: "Usage record",
    rowKey: "id",
    kicker: "USAGE & COST",
    columns: [
      { key: "id", label: "ID" },
      { key: "metric", label: "Metric" },
      { key: "value", label: "Value" },
      { key: "period", label: "Period" },
    ],
    fields: [
      { key: "id", label: "Record ID", required: true },
      { key: "metric", label: "Metric", required: true },
      { key: "value", label: "Value", required: true },
      { key: "period", label: "Period" },
    ],
  },
  health: {
    entityLabel: "Health check",
    rowKey: "service",
    kicker: "SYSTEM HEALTH",
    readOnly: true,
    columns: [
      { key: "service", label: "Service" },
      { key: "freeze", label: "Freeze" },
      { key: "note", label: "Note" },
    ],
    fields: [],
  },
  releases: {
    entityLabel: "Release",
    rowKey: "version",
    kicker: "RELEASE / VERSION",
    columns: [
      { key: "version", label: "Version" },
      { key: "label", label: "Label" },
      { key: "note", label: "Note" },
    ],
    fields: [
      { key: "version", label: "Version", required: true },
      { key: "label", label: "Label", required: true },
      { key: "note", label: "Note" },
    ],
  },
  audit: {
    entityLabel: "Audit event",
    rowKey: "event_id",
    kicker: "AUDIT LOG",
    readOnly: true,
    hideAdd: true,
    actions: { view: true, review: true, modify: false, delete: false, add: false },
    requiresApproval: { review: false },
    columns: [
      { key: "created_at", label: "When" },
      { key: "actor", label: "Actor" },
      { key: "action", label: "Action" },
      { key: "detail", label: "Detail" },
    ],
    fields: [
      { key: "created_at", label: "When" },
      { key: "actor", label: "Actor" },
      { key: "action", label: "Action" },
      { key: "detail", label: "Detail", type: "textarea" },
    ],
  },
};

export const TENANT_PANELS = {
  overview: {
    entityLabel: null,
    readOnly: true,
    kicker: "TENANT CONSOLE",
    title: "Customer administration",
    detail: "Configure company structure, users, integrations and governance for this tenant.",
  },
  company: {
    entityLabel: "Company profile",
    rowKey: "code",
    kicker: "COMPANY PROFILE",
    readOnly: true,
    hideAdd: true,
    columns: [
      { key: "customer", label: "Customer" },
      { key: "code", label: "Code" },
      { key: "product", label: "Product" },
    ],
    fields: [
      { key: "customer", label: "Customer name" },
      { key: "code", label: "Tenant code" },
      { key: "product", label: "Product" },
    ],
  },
  units: {
    entityLabel: "Business unit",
    rowKey: "id",
    kicker: "BUSINESS UNITS",
    requiresApproval: { create: false, modify: true, delete: true },
    dependencies: [{ key: "owner", label: "Owner", required: false, help: "Accountable owner for this business unit." }],
    columns: [
      { key: "id", label: "ID" },
      { key: "name", label: "Name" },
      { key: "owner", label: "Owner" },
      { key: "organization_id", label: "Organization" },
    ],
    fields: [
      { key: "id", label: "Unit ID", required: false, placeholder: "Auto if blank (BU-NRG-01-nn)" },
      { key: "name", label: "Name", required: true },
      { key: "owner", label: "Owner" },
    ],
  },
  products: {
    entityLabel: "Product",
    rowKey: "name",
    kicker: "PRODUCTS",
    readOnly: true,
    hideAdd: true,
    detail: "Licensed products are managed on the Platform Tenants panel.",
    columns: [{ key: "name", label: "Product" }],
    fields: [{ key: "name", label: "Product name", required: true }],
  },
  projects: {
    entityLabel: "Project",
    rowKey: "id",
    kicker: "PROJECTS",
    readOnly: true,
    hideAdd: true,
    detail: "Modernization projects are created from the governed workspace after MDR authorization.",
    columns: [
      { key: "id", label: "Project" },
      { key: "status", label: "Status" },
    ],
    fields: [
      { key: "id", label: "Project ID", required: true },
      { key: "status", label: "Status", type: "select", options: ["active", "planned", "closed"] },
    ],
  },
  applications: {
    entityLabel: "Application",
    rowKey: "id",
    kicker: "APPLICATIONS",
    readOnly: true,
    hideAdd: true,
    actions: { view: true, move: true, modify: false, delete: false, add: false },
    requiresApproval: { move: true },
    columns: [
      { key: "id", label: "ID" },
      { key: "name", label: "Name" },
      { key: "domain", label: "Domain" },
      { key: "stage", label: "Stage" },
      { key: "recommendation", label: "7R" },
    ],
    fields: [
      { key: "id", label: "Application ID" },
      { key: "name", label: "Name" },
      { key: "domain", label: "Domain" },
      { key: "stage", label: "Stage" },
      { key: "recommendation", label: "7R recommendation" },
    ],
  },
  users: {
    entityLabel: "User",
    rowKey: "email",
    kicker: "USERS & TEAMS",
    requiresApproval: { create: true, modify: true, delete: true, access: true },
    actions: { view: true, modify: true, delete: true, access: true },
    accessFields: ACCESS_AUTHORITY_FIELDS,
    dependencies: [{ key: "role", label: "Role", required: true, help: "Cannot assign Platform Admin or AI via self-service." }],
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "role", label: "Role" },
      { key: "scope", label: "Scope" },
    ],
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "email", label: "Email", required: true, type: "email" },
      { key: "role", label: "Role", required: true },
      { key: "scope", label: "Scope" },
      { key: "auth_G1", label: "G1 authority", default: "NONE" },
      { key: "auth_G2", label: "G2 / MDR authority", default: "NONE" },
    ],
  },
  roles: {
    entityLabel: "Role",
    rowKey: "email",
    kicker: "ROLES / RBAC",
    readOnly: true,
    hideAdd: true,
    actions: { view: true, modify: false, delete: false, add: false },
    columns: [
      { key: "role", label: "Role" },
      { key: "email", label: "Mapped user" },
      { key: "can_authorize", label: "MDR auth", format: (v) => (v ? "Yes" : "No") },
      { key: "home", label: "Home" },
    ],
    fields: [
      { key: "role", label: "Role" },
      { key: "email", label: "Email" },
      { key: "pointer", label: "Pointer", type: "textarea" },
    ],
  },
  sources: {
    entityLabel: "Data source",
    rowKey: "name",
    kicker: "DATA SOURCES",
    columns: [
      { key: "name", label: "Source" },
      { key: "state", label: "State" },
    ],
    fields: [
      { key: "name", label: "Source name", required: true },
      { key: "state", label: "State", required: true },
    ],
  },
  governance: {
    entityLabel: null,
    readOnly: true,
    kicker: "GOVERNANCE CONFIGURATION",
    title: "How to govern — not runtime decisions",
  },
  reports: {
    entityLabel: "Report",
    rowKey: "id",
    kicker: "REPORTS",
    columns: [
      { key: "id", label: "Report" },
      { key: "scope", label: "Scope" },
      { key: "note", label: "Note" },
    ],
    fields: [
      { key: "id", label: "Report ID", required: true },
      { key: "scope", label: "Scope" },
      { key: "note", label: "Note" },
    ],
  },
  audit: {
    entityLabel: "Audit event",
    rowKey: "event_id",
    kicker: "AUDIT HISTORY",
    readOnly: true,
    hideAdd: true,
    columns: [
      { key: "created_at", label: "When" },
      { key: "actor", label: "Actor" },
      { key: "action", label: "Action" },
      { key: "detail", label: "Detail" },
    ],
    fields: [
      { key: "created_at", label: "When" },
      { key: "actor", label: "Actor" },
      { key: "action", label: "Action" },
      { key: "detail", label: "Detail", type: "textarea" },
    ],
  },
};

export function formatDeployment(value) {
  if (!value) return "—";
  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function rowCompleteness(row) {
  return Object.values(row || {}).filter((v) => v != null && String(v).trim() !== "").length;
}

/** Drop rows without a primary key and dedupe — keeps the most complete row per key. */
export function normalizeCrudRows(rows, panelConfig) {
  const key = panelConfig?.rowKey || "id";
  const seen = new Map();
  for (const row of rows || []) {
    const id = String(row?.[key] ?? "").trim();
    if (!id) continue;
    const prev = seen.get(id);
    if (!prev || rowCompleteness(row) >= rowCompleteness(prev)) {
      seen.set(id, { ...row, [key]: id });
    }
  }
  return [...seen.values()].sort((a, b) => String(a[key]).localeCompare(String(b[key])));
}

export function rowStableId(row, rowKey, fallbackIndex) {
  const id = String(row?.[rowKey] ?? "").trim();
  if (id) return id;
  return `row-${fallbackIndex}`;
}

export function platformTenants(data) {
  const orgs = data?.catalog?.organizations || [];
  return orgs.flatMap((org) => (org.tenants || []).map((t) => ({
    code: t.code,
    name: t.name,
    organization: org.name,
    organization_id: org.id,
    orgId: org.id,
    product: t.product,
    subscription: t.subscription,
    deployment: t.deployment,
    region: t.region,
    data_residency: t.data_residency,
    status: t.status,
    tenant_admin: t.tenant_admin,
  })));
}

export function seedPlatformRows(panel, data, auditEvents = []) {
  const cat = data?.catalog || {};
  const orgs = cat.organizations || [];
  const tenants = platformTenants(data);

  switch (panel) {
    case "organizations":
      return orgs.map((org) => ({
        id: org.id,
        name: org.name,
        status: org.status,
        products: (org.products || []).join(", "),
      }));
    case "tenants":
      return normalizeCrudRows(tenants, PLATFORM_PANELS.tenants);
    case "subscriptions":
      return normalizeCrudRows(tenants.map((t) => ({
        code: t.code,
        name: t.name,
        subscription: t.subscription,
        product: t.product,
      })), PLATFORM_PANELS.subscriptions);
    case "users":
      return cat.directory || [];
    case "features":
      return (cat.features || []).map((f) => ({ ...f }));
    case "deployment":
      return normalizeCrudRows(tenants.map((t) => ({
        code: t.code,
        name: t.name,
        deployment: t.deployment,
        data_residency: t.data_residency,
        region: t.region,
      })), PLATFORM_PANELS.deployment);
    case "iam":
      return [
        {
          id: "session-auth",
          mode: cat.identity?.mode || "JWT httpOnly + bcrypt",
          detail: "SSO issues the same cookies. Self-registration cannot create Platform/Tenant Admin.",
        },
        {
          id: "mdr-guard",
          mode: "Decision authority only",
          detail: "AI and admin-only roles cannot authorize MDRs or pass G3–G6.",
        },
      ];
    case "ai":
      return [
        { key: "model", value: String(data?.health?.ai?.model ?? "None"), note: "Bedrock routing is Next" },
        { key: "ai_enabled", value: String(data?.health?.ai?.ai_enabled ?? false), note: "Advisory only in POC" },
      ];
    case "usage":
      return [
        { id: "tokens", metric: "Tokens", value: data?.usage?.tokens || "Not metered", period: "POC" },
        { id: "cost", metric: "Cost", value: data?.usage?.cost || "Labels only", period: "POC" },
      ];
    case "health":
      return [{
        service: data?.health?.service || "YUG360-MIND",
        freeze: data?.health?.freeze || "—",
        note: data?.health?.note || "",
      }];
    case "releases":
      return [{
        version: `v${data?.health?.freeze || "1.0"}`,
        label: "Architecture freeze",
        note: "Workspace NAV labels stay frozen. This console is additive.",
      }];
    case "audit":
      return (auditEvents || []).map((e, i) => ({
        ...e,
        event_id: e.event_id || e._id || `evt-${i}`,
      }));
    default:
      return [];
  }
}

export function seedTenantRows(panel, data, auditEvents = []) {
  const company = data?.company || {};
  switch (panel) {
    case "company":
      return [{ customer: company.customer, code: company.code, product: company.product }];
    case "units":
      return company.units || [];
    case "products":
      return (company.products || []).map((name) => ({ name }));
    case "projects":
      return company.projects || [];
    case "applications":
      return data?.applications || [];
    case "users":
      return data?.directory || [];
    case "roles":
      return (company.roles || []).map((r) => ({
        ...r,
        can_authorize: !!r.can_authorize,
      }));
    case "sources":
      return company.integrations || [];
    case "reports":
      return [
        { id: "portfolio-kpi", scope: "Workspace", note: "Portfolio KPIs remain on the product workspace." },
        { id: "governance-summary", scope: "Tenant", note: "Does not duplicate the decision chain." },
      ];
    case "audit":
      return (auditEvents || []).map((e, i) => ({
        ...e,
        event_id: e.event_id || e._id || `evt-${i}`,
      }));
    default:
      return [];
  }
}

export function emptyRow(panelConfig) {
  const row = {};
  (panelConfig.fields || []).forEach((f) => {
    if (f.type === "checkbox") row[f.key] = false;
    else row[f.key] = f.default ?? "";
  });
  return row;
}

export function validateCrudForm(form, panelConfig) {
  const errors = [];
  for (const field of panelConfig.fields || []) {
    if (!field.required) continue;
    const val = form[field.key];
    if (val == null || String(val).trim() === "") {
      errors.push(`${field.label} is required`);
    }
  }
  return errors;
}

export function displayCell(row, col) {
  const raw = row[col.key];
  if (col.format) return col.format(raw, row);
  if (typeof raw === "boolean") return raw ? "Yes" : "No";
  if (raw == null || raw === "") return "—";
  return String(raw);
}
